import { Inject, Injectable, InjectionToken, Optional } from "@angular/core";
import { Router } from "@angular/router";
import { DateHelper } from "@calaosoft/osapp-common/dates/helpers/dateHelper";
import { Entity } from "@calaosoft/osapp-common/entities/models/entity";
import { EReminderAlarmActionType } from "@calaosoft/osapp-common/reminder-alarms/models/ereminder-alarm-action-type";
import { ReminderAlarm } from "@calaosoft/osapp-common/reminder-alarms/models/reminder-alarm";
import { IDataSource } from "@calaosoft/osapp-common/store/models/IDataSource";
import { EDatabaseRole } from "@calaosoft/osapp-common/store/models/edatabase-role";
import { IStoreDocument } from "@calaosoft/osapp-common/store/models/istore-document";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import {
	ActionPerformed,
	Channel,
	LocalNotificationDescriptor,
	LocalNotifications
} from "@capacitor/local-notifications";
import { Observable, Subject, Subscription, firstValueFrom, merge } from "rxjs";
import { tap } from "rxjs/operators";
import { IApplicationEvent } from "../../../model/application/IApplicationEvent";
import { EConfigFlag } from "../../../model/config/econfig-flag";
import { ActivePageManager } from "../../../model/navigation/ActivePageManager";
import { ILocalNotificationAction } from "../../../model/notification/ilocal-notification-action";
import { EChangeType } from "../../../model/store/EChangeType";
import { EStoreFlag } from "../../../model/store/EStoreFlag";
import { IChangeEvent } from "../../../model/store/IChangeEvent";
import { FlagService } from "../../../services/flag.service";
import { NotificationService } from "../../../services/notification.service";
import { PlatformService } from "../../../services/platform.service";
import { Store } from "../../../services/store.service";
import { EEntityFlag } from "../../entities/models/eentity-flag";
import { EntitiesQueryService } from "../../entities/services/entities-query.service";
import { EntitiesService } from "../../entities/services/entities.service";
import { EventsService } from "../../events/events.service";
import { EFlag } from "../../flags/models/EFlag";
import { DestroyableServiceBase } from "../../services/models/destroyable-service-base";
import { IDataSourceRemoteChanges } from "../../store/model/IDataSourceRemoteChanges";
import { DumpApplicationEvent } from "../../store/model/dump-application-event";
import { EDumpStatus } from "../../store/model/edump-status";
import { IDumpApplicationEventData } from "../../store/model/idump-application-event-data";
import { EReminderAlarmAction } from "../models/ereminder-alarm-action";
import { IAlarm } from "../models/i-alarm";
import { IReminderAlarmActionPerformed } from "../models/ireminder-alarm-action-performed";
import { IReminderAlarmsConfig } from "../models/ireminder-alarms-config";

/** La configuration des entités sur lesquelles des rappels peuvent être définis se fait par application.
 *  Ce service ne s'initialisera pas pour une application qui ne propose pas de configuration.
 */
export const REMINDERS_CONFIG = new InjectionToken<IReminderAlarmsConfig>("REMINDERS_CONFIG");

/** Service de gestion des rappels. Doit être initialisé le plus tôt possible.
 * Ecoute en continu les changements (locaux ET distants) de certaines entités sur la base de workspace,
 *  afin de créer/détruire les notifications locales de rappel.
 * Détecte automatiquement l'application d'un dump et force la mise à jour des notifications locales dans ce cas.
 */
@Injectable({
	// On ne veut qu'une seule instance de ce service pour toute l'app
	providedIn: "root"
})
export class ReminderAlarmsService extends DestroyableServiceBase {
	//#region FIELDS

	/** Identifiant de log */
	private static readonly C_LOG_ID = "RMD.S::";
	private static readonly C_STORAGE_LAST_REMINDER_SYSTEM_ID = "reminders.lastSystemId";
	private static readonly C_STORAGE_LAST_FULL_UPDATE = "reminders.lastFullUpdate";
	/** Version de la base IndexedDB, à incrémenter en cas de màj du schéma */
	private static readonly C_DB_VERSION = 1;
	private static readonly C_DB_NAME = "reminders";
	private static readonly C_ALARMS_STORE_NAME = "alarms";
	private static readonly C_ALARMS_INDEX_BY_ENTITY = "byEntityId";
	/** Nombre de jours sur lesquels seront générés les rappels (par défaut on génère pour 1 mois) */
	private static readonly C_MAX_GENERATION_RANGE_DAYS = 30;
	/** Fréquence de recréation des rappels (par défaut recréés tous les jours) */
	private static readonly C_MAX_REFRESH_INTERVAL_DAYS = 1;

	// Actions possibles sur les notifications de rappel, groupées par type

	private static readonly C_RESCHEDULE_ACTIONS: ILocalNotificationAction[] = [
		{
			id: EReminderAlarmAction.reschedule,
			title: "Reporter",
			foreground: true
		}
	];

	private static readonly C_REPEAT_ACTIONS: ILocalNotificationAction[] = [
		{
			id: EReminderAlarmAction.repeatAfter5mn,
			title: "Dans 5min.",
			foreground: true
		},
		{
			id: EReminderAlarmAction.repeatAfter15mn,
			title: "Dans 15min.",
			foreground: true
		}
	];

	private maRemindables: string[];
	private remindablesChangesSubscription: Subscription | undefined;
	private moDB: IDBDatabase | undefined;
	private mbResetInProgress = false;
	private mbInitialized = false;

	private moActionsPerformed$ = new Subject<IReminderAlarmActionPerformed>();

	//#endregion

	//#region PROPERTIES

	public static readonly C_DEFAULT_CHANNEL_ID = "reminders";
	/** Niveau d'importance minimal pour avoir avoir une notification sous forme de bannière par défaut sur Android. */
	public static readonly C_RECOMMENDED_CHANNEL_IMPORTANCE = 4;

	/** Observable remontant toutes les actions faites par l'utilisateur sur des notifications de rappel */
	public get actionsPerformed$(): Observable<IReminderAlarmActionPerformed> {
		return this.moActionsPerformed$.asObservable();
	}

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcFlag: FlagService,
		private readonly isvcStore: Store,
		private readonly ioRouter: Router,
		private readonly isvcEvents: EventsService,
		private readonly isvcPlatform: PlatformService,
		private readonly isvcNotifications: NotificationService,
		private readonly isvcEntitiesQuery: EntitiesQueryService,
		private readonly isvcEntities: EntitiesService,
		@Inject(REMINDERS_CONFIG) @Optional() private readonly ioConfig?: IReminderAlarmsConfig
	) {
		super();
		this.maRemindables = ioConfig?.entitiesPrefixes ?? [];
	}

	//#region INITIALISATION

	/** Initialise les rappels sur les entités supportées, pour toute l'app sur toute sa durée de vie en mémoire.
	 * DOIT ETRE APPELEE AVANT L'INITIALISATION DES BASES
	 */
	public async initAsync(): Promise<void> {
		console.debug(`${ReminderAlarmsService.C_LOG_ID}Initialisation...`);

		// On attend que la config d'appli soit montée en mémoire pour choisir de démarrer ou non le service
		await this.isvcFlag.waitForFlagAsync(EConfigFlag.StaticConfigReady, true);

		// Le service ne sera démarré que sur mobile, uniquement pour les apps qui indiquent explicitement l'utiliser
		if (this.isvcPlatform.isMobileApp && this.ioConfig && !this.mbInitialized) {
			// On empêche les mutliples initialisations
			this.mbInitialized = true;

			// On initialise les notfications locales, pour bénéficier du routage à l'ouverture des notifs
			await this.isvcNotifications.initLocalNotifications();

			// Initialisation des canaux de notifications (Android)
			await this.initNotificationChannelsAsync();

			// On initialise les actions, pour pouvoir bénéficier des boutons d'action sur les notifs
			await this.initNotificationActionsAsync();

			// Détection de l'application d'un dump (lorsqu'un dump est appliqué, tous les rappels sont reconstruits d'un seul coup)
			this.isvcEvents.events$.subscribe((poApplicationEvent: IApplicationEvent) => {
				if (poApplicationEvent instanceof DumpApplicationEvent) {
					const loEventData: IDumpApplicationEventData = (poApplicationEvent as DumpApplicationEvent).data;
					if (loEventData.database.hasRole(EDatabaseRole.workspace) && loEventData.status === EDumpStatus.finished) {
						console.debug(`${ReminderAlarmsService.C_LOG_ID} Dump workspace terminé`);
						// Génération initiale de tous les rappels suite à l'application d'un dump sur le workspace
						this.resetAllAlarmsAsync();
					}
				}
			});

			// On attend que la base de WS soit initialisée après login pour écouter les changements. On reste abonné "à vie" pour gérer les déconnexions/reconnexions
			this.isvcFlag
				.waitForFlag(EStoreFlag.DBInitialized, true)
				.pipe(
					tap(async (_) => {
						// Si la dernière génération complète des rappels est trop ancienne, on remet tout à jour
						if (this.needFullUpdate()) {
							console.debug(`${ReminderAlarmsService.C_LOG_ID}Grand reset nécessaire`);
							await this.resetAllAlarmsAsync();
						}
						this.listenForRemindablesChanges();
					})
				)
				.subscribe();

			try {
				this.moDB = await this.initIndexedDBAsync();
				console.debug(`${ReminderAlarmsService.C_LOG_ID}Initialisé !`);
			} catch (poError) {
				console.error(`${ReminderAlarmsService.C_LOG_ID}Impossible d'initialiser IndexedDB`);
			}
		} else {
			console.debug(
				`${ReminderAlarmsService.C_LOG_ID}Initialisation avortée. (webapp ou application n'utilisant pas les rappels)`
			);
		}
	}

	private async initNotificationChannelsAsync(): Promise<void> {
		// Il ne faut pas créer de canaux sur iOS, ce n'est pas supporté et empêche le fonctionnement des notifs.
		if (this.isvcPlatform.isAndroid) {
			const loPartialChannels: Partial<Channel> | undefined = this.ioConfig?.channels;
			if (loPartialChannels) {
				const laChannelIds: string[] = Object.keys(loPartialChannels);
				for (const lsPartialChannel of laChannelIds) {
					await LocalNotifications.createChannel({ ...loPartialChannels[lsPartialChannel], id: lsPartialChannel });
				}
			}
		}
	}

	private async initNotificationActionsAsync(): Promise<void> {
		// Callback appelée lorsqu'un bouton d'action sur une notification locale est utilisé
		this.isvcNotifications.localNotificationActions$.subscribe(async (poAction: ActionPerformed) => {
			// On contrôle que l'action corresponde bien à celles définies dans ce service (sinon cela ne nous concerne pas et on ne fait rien)
			if ((Object.values(EReminderAlarmAction) as string[]).includes(poAction.actionId)) {
				// On bloque l'émission de l'évènement tant que l'app n'est pas correctement initialisée pour être certain que les services à l'écoute seront en mesure de le gérer
				await this.isvcFlag.waitForFlagAsync(EFlag.appAvailable, true);
				// Cast explicite en EReminderAction car appartenance contrôlée manuellement ci-dessus
				this.moActionsPerformed$.next({
					action: poAction.actionId as EReminderAlarmAction,
					notificationExtras: poAction.notification.extra
				});
			}
		});

		/*
		On enregistre auprès du plugin des types d'actions que pourront porter les notifications émises par la suite.
		Attention à ne pas dépasser 3 actions par type, sinon Android n'affichera pas tous les boutons.
		*/
		await LocalNotifications.registerActionTypes({
			types: [
				{
					id: EReminderAlarmActionType.reschedule,
					actions: ReminderAlarmsService.C_RESCHEDULE_ACTIONS
				},
				{
					id: EReminderAlarmActionType.repeat,
					actions: ReminderAlarmsService.C_REPEAT_ACTIONS
				},
				{
					id: EReminderAlarmActionType.rescheduleAndRepeat,
					actions: [...ReminderAlarmsService.C_RESCHEDULE_ACTIONS, ...ReminderAlarmsService.C_REPEAT_ACTIONS]
				}
			]
		});
	}

	private listenForRemindablesChanges(): void {
		console.debug(`${ReminderAlarmsService.C_LOG_ID}Ecoute des changements sur le workspace`);

		// Cette méthode est bindée sur un évènement qu'on ne maitrise pas, si jamais elle a déjà été appelée on libère le flux précédent pour éviter les fuites mémoire
		if (this.remindablesChangesSubscription) this.remindablesChangesSubscription.unsubscribe();

		// On crée un datasource par type de donnée "Remindable"
		let loRemindableChanges$: Observable<IChangeEvent<IStoreDocument>> | undefined;
		this.maRemindables.forEach((poRemindable) => {
			const loRemindableDataSource: IDataSourceRemoteChanges = {
				databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace),
				remoteChanges: true,
				live: true,
				activePageManager: new ActivePageManager(this, this.ioRouter, () => true),
				viewParams: {
					include_docs: true,
					startkey: poRemindable,
					endkey: `${poRemindable}${Store.C_ANYTHING_CODE_ASCII}`
				}
			};

			// Les requêtes sont faites au travers du service de requêtage des entités, qui permet d'obtenir des instances de classe pour les données que l'on requête.
			// Attention à bien laisser le paramètre permettant de requêter les entités liées
			const loChanges$: Observable<IChangeEvent<IStoreDocument>> = this.isvcEntitiesQuery.getEntriesChanges$(
				loRemindableDataSource,
				undefined,
				true,
				true
			);

			// On merge toutes les remontées de données dans un même flux
			if (!loRemindableChanges$) loRemindableChanges$ = loChanges$;
			else loRemindableChanges$ = merge(loRemindableChanges$, loChanges$);
		});

		this.remindablesChangesSubscription = loRemindableChanges$?.subscribe(this.onRemindableChange.bind(this));
	}

	//#endregion

	//#region CALLBACKS

	private onRemindableChange(poChangeEvent: IChangeEvent<Entity>): void {
		if (!this.mbResetInProgress) {
			console.debug(`${ReminderAlarmsService.C_LOG_ID} Changement détecté pour ${poChangeEvent.document._id}`);

			switch (poChangeEvent.changeType) {
				case EChangeType.create:
				case EChangeType.update:
					const loRemindable: Entity = poChangeEvent.document;
					this.setAlarmsAsync(loRemindable);
					break;
				case EChangeType.delete:
					this.cancelAlarmsAsync(poChangeEvent.document._id);
					// Dans ce cas, supprimer tous les reminders liés à la donnée
					break;
				default:
					console.debug(`${ReminderAlarmsService.C_LOG_ID}Changement non géré`);
			}
		}
	}

	//#endregion

	//#region INDEXEDDB

	private initIndexedDBAsync(): Promise<IDBDatabase> {
		// IndexedDB propose une API à base de callbacks, on "promessifie" pour homogénéiser le code
		let lfResolve, lfReject;
		const loReturnPromise = new Promise<IDBDatabase>((pfResolve, pfReject) => {
			lfResolve = pfResolve;
			lfReject = pfReject;
		});

		const loOpenRequest = indexedDB.open(ReminderAlarmsService.C_DB_NAME, ReminderAlarmsService.C_DB_VERSION);

		loOpenRequest.onupgradeneeded = () => {
			const loDb: IDBDatabase = loOpenRequest.result;

			const loChangesObjectStore: IDBObjectStore = loDb.createObjectStore(ReminderAlarmsService.C_ALARMS_STORE_NAME, {
				keyPath: ["entityId", "targetDate"]
			});
			loChangesObjectStore.createIndex(ReminderAlarmsService.C_ALARMS_INDEX_BY_ENTITY, "entityId");
		};

		loOpenRequest.onerror = () => lfReject(loOpenRequest.error);

		loOpenRequest.onsuccess = () => lfResolve(loOpenRequest.result);

		return loReturnPromise;
	}

	private getStoreFromDB(psStore: string): IDBObjectStore {
		// Ouverture d'une transaction vers le store des Alarmes dans IndexedDB
		if (!this.moDB)
			throw new Error("Impossible d'accéder au Store des ReminderAlarms car IndexedDB n'a pas été initialisé.");
		const loTransaction = this.moDB.transaction(psStore, "readwrite");

		// Accès au store des Alarmes
		return loTransaction.objectStore(psStore);
	}

	private deleteAlarmsFromDBAsync(psEntityId: string): Promise<void> {
		// IndexedDB propose une API à base de callbacks, on "promessifie" pour homogénéiser le code
		let lfResolve, lfReject;
		const loReturnPromise = new Promise<void>((pfResolve, pfReject) => {
			lfResolve = pfResolve;
			lfReject = pfReject;
		});

		const loAlarmsStore = this.getStoreFromDB(ReminderAlarmsService.C_ALARMS_STORE_NAME);
		// On utilise un curseur pour parcourir l'index des entités
		const loCursorRequest = loAlarmsStore
			.index(ReminderAlarmsService.C_ALARMS_INDEX_BY_ENTITY)
			.openKeyCursor(psEntityId);

		loCursorRequest.onsuccess = () => {
			const loCursor = loCursorRequest.result;
			if (loCursor) {
				// On supprime chaque entrée correspondant à l'id d'entité
				loAlarmsStore.delete(loCursor.primaryKey);
				loCursor.continue();
			} else lfResolve();
		};

		loCursorRequest.onerror = () => lfReject(loCursorRequest.error);

		return loReturnPromise;
	}

	/** Utilitaire pour rendre plus lisibles les requêtes à IndexedDB. Permet d'utiliser des promesses au lieu des callbacks. */
	private IDBRequestToPromise<T = any>(poRequest: IDBRequest<T>): Promise<T> {
		// IndexedDB propose une API à base de callbacks, on "promessifie" pour homogénéiser le code
		let lfResolve, lfReject;
		const loReturnPromise = new Promise<T>((pfResolve, pfReject) => {
			lfResolve = pfResolve;
			lfReject = pfReject;
		});

		poRequest.onsuccess = () => lfResolve(poRequest.result);
		poRequest.onerror = () => lfReject(poRequest.error);

		return loReturnPromise;
	}

	private async clearStoreInDBAsync(psStore: string) {
		await this.IDBRequestToPromise(this.getStoreFromDB(psStore).clear());
	}

	private async putAlarmInDBAsync(poAlarm: IAlarm): Promise<void> {
		// Requête d'ajout de l'Alarme en base
		await this.IDBRequestToPromise(this.getStoreFromDB(ReminderAlarmsService.C_ALARMS_STORE_NAME).add(poAlarm));
	}

	private async getAlarmsFromDBAsync(psEventId: string): Promise<IAlarm[]> {
		return await this.IDBRequestToPromise(
			this.getStoreFromDB(ReminderAlarmsService.C_ALARMS_STORE_NAME)
				.index(ReminderAlarmsService.C_ALARMS_INDEX_BY_ENTITY)
				.getAll(psEventId)
		);
	}

	private async getAllAlarmsFromDBAsync(): Promise<IAlarm[]> {
		return await this.IDBRequestToPromise(this.getStoreFromDB(ReminderAlarmsService.C_ALARMS_STORE_NAME).getAll());
	}

	//#endregion

	//#region ALARMS

	/** Supprime toutes les notifications planifiées (s'il y en a) et crée toutes les notifications à partir des données du workspace
	 */
	private async resetAllAlarmsAsync(): Promise<void> {
		console.debug(`${ReminderAlarmsService.C_LOG_ID} (Re)setting all alarms...`);
		if (!this.mbResetInProgress) {
			this.mbResetInProgress = true;
			const laSystemIdsToDelete: LocalNotificationDescriptor[] = (await this.getAllAlarmsFromDBAsync()).map(
				(poAlarm) => {
					return { id: poAlarm.systemId };
				}
			);
			// S'il y a effectivement des notifications à déprogrammer
			if (ArrayHelper.hasElements(laSystemIdsToDelete)) {
				console.debug(`${ReminderAlarmsService.C_LOG_ID} Unplanning all Alarms`);

				// Les annuler dans l'os
				LocalNotifications.cancel({ notifications: laSystemIdsToDelete });

				// Tout supprimer d'indexedDb pour cet id
				await this.clearStoreInDBAsync(ReminderAlarmsService.C_ALARMS_STORE_NAME);
			}

			// Requêtage de tous les Remindables présents en base

			// On crée un datasource par type de donnée "Remindable"
			await Promise.all(
				this.maRemindables.map(async (poRemindable) => {
					const loRemindableDataSource: IDataSource = {
						databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace),
						live: false,
						viewParams: {
							include_docs: true,
							startkey: poRemindable,
							endkey: `${poRemindable}${Store.C_ANYTHING_CODE_ASCII}`
						},
						withExternal: true
					};

					// Les requêtes sont faites au travers du service de requêtage des entités, qui permet d'obtenir des instances de classe pour les données que l'on requête.
					// Attention à bien laisser le paramètre permettant de requêter les entités liées
					const laRemindables: Entity[] | undefined = await firstValueFrom(
						this.isvcEntitiesQuery.getEntries$(loRemindableDataSource)
					);
					console.debug(`${ReminderAlarmsService.C_LOG_ID} Tous les Remindables ${poRemindable} ont été collectés`);

					// On programme tous les rappels pour ce type d'entité
					await Promise.all(
						laRemindables?.map((poRemindable) => {
							return this.setAlarmsAsync(poRemindable);
						})
					);
				})
			);

			// Mise à jour de la date de dernière update complète
			this.setLastUpdate();
			console.debug(`${ReminderAlarmsService.C_LOG_ID} All alarms setted from workspace`);
			this.mbResetInProgress = false;
		} else {
			console.debug(`${ReminderAlarmsService.C_LOG_ID} Reset canceled`);
		}
	}

	/** (Re)crée toutes les notifications (Alarmes) pour une entité "Remindable" donnée */
	private async setAlarmsAsync(poRemindable: Entity): Promise<void> {
		console.debug(`${ReminderAlarmsService.C_LOG_ID} Planning all reminders for : ${poRemindable.id}`);

		// Pour éviter des latences et traitements inutiles, on vérifie s'il y a besoin du descripteur d'entité pour générer les rappels
		if (!poRemindable.hasCustomRemindersImplementation) {
			// On attend l'init du service d'entités
			await this.isvcFlag.waitForFlagAsync(EEntityFlag.entitiesReady, true);
			// Il faut demander explicitement le calcul des rappels dans ce cas
			await this.isvcEntities.fillRemindersAsync(poRemindable);
		}

		const laReminders: ReminderAlarm[] = poRemindable.getReminders(
			DateHelper.addDays(new Date(), ReminderAlarmsService.C_MAX_GENERATION_RANGE_DAYS)
		);

		// Suppression (ne fait rien s'il n'y a rien à supprimer)
		await this.cancelAlarmsAsync(poRemindable.id);

		// Création de toutes les notifications dont la date n'est pas déjà passée
		const ldNow = new Date();
		laReminders
			.filter((poReminder) => poReminder.date >= ldNow)
			.forEach(async (poReminder) => {
				await this.scheduleAlarmAsync(poReminder);
			});
	}

	/** Permet de planifier une notification (OS) à partir d'un Reminder */
	private async scheduleAlarmAsync(poReminder: ReminderAlarm): Promise<void> {
		const lnSystemId: number = this.buildNextAlarmSystemId();

		LocalNotifications.schedule({
			notifications: [
				{
					title: poReminder.title,
					body: poReminder.description ?? "",
					id: lnSystemId,
					schedule: {
						at: poReminder.date,
						allowWhileIdle: true // Pour android : permet d'éviter des retards conséquents lorsque l'appareil est en veille
					},
					extra: poReminder.notificationExtra,
					actionTypeId: poReminder.actionType,
					channelId: this.getChannelId(poReminder)
				}
			]
		});

		await this.putAlarmInDBAsync({
			entityId: poReminder.entityId,
			targetDate: poReminder.date,
			systemId: lnSystemId,
			update: new Date()
		});
	}

	/**
	 * @returns L'identifiant du canal où publier la notification. `undefined` si aucun canal valide configuré.
	 */
	private getChannelId(poReminder: ReminderAlarm): string | undefined {
		const lsCandidateChannelId = poReminder.channelId ?? ReminderAlarmsService.C_DEFAULT_CHANNEL_ID;
		if (this.ioConfig?.channels && this.ioConfig.channels[lsCandidateChannelId]) return lsCandidateChannelId;
		else return undefined;
	}

	private async cancelAlarmsAsync(psRemindableEntityId: string): Promise<void> {
		// Requêter les alarmes qui ont cet id d'entité en elles
		const ldNow = new Date();
		const laSystemIdsToDelete: LocalNotificationDescriptor[] = (await this.getAlarmsFromDBAsync(psRemindableEntityId))
			.filter((poAlarm) => poAlarm.targetDate > ldNow) // On ne cancel dans l'os que les alarmes qui ne sont pas encore passées, pour éviter les disparitions intempestives de notifications
			.map((poAlarm) => {
				return { id: poAlarm.systemId };
			});

		// S'il y a effectivement des notifications à déprogrammer
		if (ArrayHelper.hasElements(laSystemIdsToDelete)) {
			console.debug(`${ReminderAlarmsService.C_LOG_ID}Reminders to unplan : ${psRemindableEntityId}`);

			// Annulation dans l'os
			await LocalNotifications.cancel({ notifications: laSystemIdsToDelete });
		}

		// Tout supprimer d'indexedDb pour cet id
		await this.deleteAlarmsFromDBAsync(psRemindableEntityId);
	}

	//#endregion

	//#region LOCALSTORAGE

	/** Unique générateur d'identifiant de notification locale. A utiliser pour chaque création de notification. */
	private buildNextAlarmSystemId(): number {
		// Obtention de l'id courant (0 par défaut s'il n'existe pas) et incrément de 1
		const lnId = +(localStorage.getItem(ReminderAlarmsService.C_STORAGE_LAST_REMINDER_SYSTEM_ID) ?? "0") + 1;
		localStorage.setItem(ReminderAlarmsService.C_STORAGE_LAST_REMINDER_SYSTEM_ID, lnId.toString());
		return lnId;
	}

	/** Permet de savoir si on a dépassé l'âge maximum admissible des rappels déjà créés */
	private needFullUpdate(): boolean {
		const lastUpdateString = localStorage.getItem(ReminderAlarmsService.C_STORAGE_LAST_FULL_UPDATE);
		if (lastUpdateString)
			return (
				new Date(lastUpdateString) <
				DateHelper.addDays(new Date(), -1 * ReminderAlarmsService.C_MAX_REFRESH_INTERVAL_DAYS)
			);
		else return true;
	}

	/** Met à jour la date de dernière mise à jour de la totalité des rappels */
	private setLastUpdate(): void {
		localStorage.setItem(ReminderAlarmsService.C_STORAGE_LAST_FULL_UPDATE, new Date().toISOString());
	}

	//#endregion

	//#endregion
}
